import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchItems } from '../queries';
import { RootState } from '../store';
import { SalesforceSalesLevels, SalesforceSalesLevelPath, flattenSalesLevelTree } from '../../types/salesforce';

interface SalesforceSalesLevelState {
  salesforceSalesLevels: SalesforceSalesLevels;
  loading: boolean;
  error: string | null;
}

const initialState: SalesforceSalesLevelState = {
  salesforceSalesLevels: {},
  loading: false,
  error: null,
};

export const fetchSalesforceSalesLevels = createAsyncThunk<SalesforceSalesLevels, void, { state: RootState }>(
  'salesforceSalesLevels/fetchSalesforceSalesLevels',
  async (_: void, { getState }) => {
    const state = getState() as RootState;
    const fetchedSalesLevels = await fetchItems('sf/sales-levels', { email: state.user.userData.email });
    return fetchedSalesLevels;
  }
);

export const checkSalesforceSalesLevelsLoaded = () => async (dispatch, getState) => {
  const state = getState() as RootState;
  if (Object.keys(state.salesforceSalesLevels.salesforceSalesLevels).length === 0 && !state.salesforceSalesLevels.loading) {
    await dispatch(fetchSalesforceSalesLevels());
  }
};

// Selector: get all possible sales level paths for dropdowns, etc
export const selectFlattenedSalesLevelPaths = (state: RootState): SalesforceSalesLevelPath[] => {
  return flattenSalesLevelTree(state.salesforceSalesLevels.salesforceSalesLevels);
};

// Selector: get the raw sales level tree
export const selectSalesforceSalesLevelTree = (state: RootState): SalesforceSalesLevels =>
  state.salesforceSalesLevels.salesforceSalesLevels;

const salesforceSalesLevelsSlice = createSlice({
  name: 'salesforceSalesLevels',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesforceSalesLevels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesforceSalesLevels.fulfilled, (state, action: PayloadAction<SalesforceSalesLevels>) => {
        state.salesforceSalesLevels = action.payload;
        state.loading = false;
      })
      .addCase(fetchSalesforceSalesLevels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      });
  },
});

export default salesforceSalesLevelsSlice.reducer;